import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { photographerAPI } from '@services/photographerApi';
import { useOnboardingReturn } from '@hooks/useOnboardingReturn';

type Availability = 'idle' | 'checking' | 'available' | 'taken';

const BusinessProfileSettings: React.FC = () => {
  const returnToOnboarding = useOnboardingReturn();
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [initialSubdomain, setInitialSubdomain] = useState('');
  const [availability, setAvailability] = useState<Availability>('idle');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const debounce = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await photographerAPI.getProfile();
        if (cancelled) return;
        const p = res.data;
        setBusinessName(p.businessName || '');
        setPhone(p.contactInfo?.phone || '');
        setWebsite(p.contactInfo?.website || '');
        const sub = p.domainConfig?.subdomain || '';
        setSubdomain(sub);
        setInitialSubdomain(sub);
      } catch {
        // leave fields blank; user can still enter values
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!subdomain || subdomain === initialSubdomain) {
      setAvailability('idle');
      return;
    }
    setAvailability('checking');
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await photographerAPI.checkSubdomainAvailability(subdomain);
        setAvailability(res.data.available ? 'available' : 'taken');
      } catch {
        setAvailability('idle');
      }
    }, 400);
    return () => clearTimeout(debounce.current);
  }, [subdomain]);

  const save = async () => {
    setSaving(true);
    try {
      await photographerAPI.updateProfile({
        businessName,
        contactInfo: { name: businessName, phone, website },
      });
      if (subdomain && subdomain !== initialSubdomain && availability === 'available') {
        await photographerAPI.updateDomainConfig({ subdomain });
        setInitialSubdomain(subdomain);
      }
      toast.success('Profile saved');
      returnToOnboarding();
    } catch {
      toast.error('Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <h2 className="text-xl font-semibold text-gray-900">Business profile</h2>

      <div>
        <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
          Business name
        </label>
        <input
          id="businessName"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </div>
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          Phone
        </label>
        <input
          id="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </div>
      <div>
        <label htmlFor="website" className="block text-sm font-medium text-gray-700">
          Website
        </label>
        <input
          id="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </div>
      <div>
        <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700">
          Subdomain
        </label>
        <div className="mt-1 flex items-center">
          <input
            id="subdomain"
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
            className="w-full rounded-md border border-gray-300 px-3 py-2"
          />
          <span className="ml-2 text-sm text-gray-500">.pixel-share.com</span>
        </div>
        {availability === 'checking' && (
          <p className="mt-1 text-xs text-gray-400">Checking…</p>
        )}
        {availability === 'available' && (
          <p className="mt-1 text-xs text-green-600">Available</p>
        )}
        {availability === 'taken' && (
          <p className="mt-1 text-xs text-red-600">Already taken</p>
        )}
      </div>

      <button
        onClick={save}
        disabled={loading || saving || !businessName.trim()}
        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : loading ? 'Loading…' : 'Save profile'}
      </button>
    </div>
  );
};

export default BusinessProfileSettings;
